import { computed, onMounted, onUnmounted, ref, watchEffect, type ComputedRef, type Ref } from 'vue'
import { FEATURES } from '@/constants/features'
import {
  readStoredThemeMode,
  resolveIsDark,
  safeLocalStorage,
  writeStoredThemeMode,
  type ThemeMode,
} from '@/utils/theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'

// Состояние общее для всего приложения: переключатель в настройках и синхронизация в App.vue.
// systemPrefersDark читаем сразу, иначе первый watchEffect снимет класс, поставленный index.html.
const mode = ref<ThemeMode>(readStoredThemeMode(safeLocalStorage()))
const systemPrefersDark = ref(window.matchMedia(DARK_QUERY).matches)
const isDark = computed(() => FEATURES.darkTheme && resolveIsDark(mode.value, systemPrefersDark.value))

export interface UseThemeReturn {
  mode: Readonly<Ref<ThemeMode>>
  isDark: ComputedRef<boolean>
  setMode: (next: ThemeMode) => void
}

/**
 * Текущая тема и её смена.
 * @example const { mode, setMode } = useTheme(); setMode('dark')
 */
export function useTheme(): UseThemeReturn {
  function setMode(next: ThemeMode): void {
    mode.value = next
    writeStoredThemeMode(safeLocalStorage(), next)
  }
  return { mode, isDark, setMode }
}

/**
 * Держит класс `dark` на <html> в соответствии с темой и следит за системной настройкой.
 * Вызывать один раз — в App.vue.
 */
export function useThemeSync(): void {
  const media = window.matchMedia(DARK_QUERY)
  const onChange = (event: MediaQueryListEvent): void => {
    systemPrefersDark.value = event.matches
  }
  onMounted(() => media.addEventListener('change', onChange))
  onUnmounted(() => media.removeEventListener('change', onChange))
  watchEffect(() => {
    document.documentElement.classList.toggle('dark', isDark.value)
  })
}
